import Phaser from "phaser";
import { Item, SquadMemberDetail, SquadUpdateData, getHatSpriteKey } from "../models/common";
import { COLORS, hexToPhaser } from "./colors";

const SQUAD_SCALE = 2.6;
const SQUAD_SCALE_RATIO = SQUAD_SCALE / 3.2;
const SQUAD_OFFSET_X = 55;

/**
 * Renders a squad partner's sprite (skin/hat/weapon), name, and status label
 * to the left of and behind a host screen's own player pedestal. Used by both
 * DeployScreen and RaiderScreen so the two screens render the partner
 * identically and independently receive live squad-update pushes.
 *
 * The host is responsible for inserting getDisplayObjects() into its own
 * panel.add([...]) call at the right position (between its pedestal and its
 * own player sprite) - this class never touches panel add-order itself.
 */
export class SquadPartnerDisplay {
  private readonly scene: Phaser.Scene;
  private readonly panel: Phaser.GameObjects.Container;
  private readonly pedestalY: number;
  private readonly squadPlayerX: number;
  private readonly idleAnimKey: string;

  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly weaponSprite: Phaser.GameObjects.Sprite;
  private readonly hatSprite: Phaser.GameObjects.Sprite;
  private nameText: Phaser.GameObjects.Text | null = null;
  private nameBg: Phaser.GameObjects.Graphics | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private statusBg: Phaser.GameObjects.Graphics | null = null;

  private screenVisible: boolean = false;
  private otherMember: SquadMemberDetail | null = null;
  private otherMemberActiveSkin: string | undefined;
  private otherMemberEquippedHat: number | undefined;
  private otherMemberWeapon: Item | null | undefined = null;

  constructor(
    scene: Phaser.Scene,
    panel: Phaser.GameObjects.Container,
    anchorX: number,
    pedestalY: number,
    animKeyPrefix: string,
  ) {
    this.scene = scene;
    this.panel = panel;
    this.pedestalY = pedestalY;
    this.squadPlayerX = anchorX - SQUAD_OFFSET_X;
    this.idleAnimKey = `${animKeyPrefix}_squad_idle`;

    // Mirrored (negative scaleX) so the partner faces the main player to their right.
    // Scale-based flipping (rather than setFlipX) is used because weaponSprite's
    // origin isn't centered - a true geometric mirror is needed so the weapon
    // ends up on the correct side of the body instead of just flipping its pixels
    // in place.
    this.sprite = scene.add.sprite(
      this.squadPlayerX,
      pedestalY - 42 * SQUAD_SCALE_RATIO,
      "player",
      0,
    );
    this.sprite.setScale(-SQUAD_SCALE, SQUAD_SCALE);
    this.sprite.setVisible(false);

    this.weaponSprite = scene.add.sprite(
      this.squadPlayerX,
      pedestalY - 22 * SQUAD_SCALE_RATIO,
      "weapon",
      0,
    );
    this.weaponSprite.setOrigin(0, 0.5);
    this.weaponSprite.setScale(-2.4 * SQUAD_SCALE_RATIO, 2.4 * SQUAD_SCALE_RATIO);
    this.weaponSprite.setVisible(false);

    this.hatSprite = scene.add.sprite(
      this.squadPlayerX,
      pedestalY - 93.2 * SQUAD_SCALE_RATIO + 42 * SQUAD_SCALE_RATIO,
      "hat-1",
      0,
    );
    this.hatSprite.setOrigin(0.5, 1);
    this.hatSprite.setScale(-SQUAD_SCALE, SQUAD_SCALE);
    this.hatSprite.setVisible(false);
  }

  /** Insert these into the host's own panel.add([...]) between its pedestal and player sprite. */
  getDisplayObjects(): Phaser.GameObjects.GameObject[] {
    return [this.sprite, this.weaponSprite, this.hatSprite];
  }

  getOtherMember(): SquadMemberDetail | null {
    return this.otherMember;
  }

  updateState(memberDetails: SquadMemberDetail[], myAccountId: number | undefined): void {
    const otherMember = memberDetails.length === 2
      ? memberDetails.find((m) => m.accountId !== myAccountId) ?? null
      : null;

    if (!otherMember || otherMember.accountId !== this.otherMember?.accountId) {
      // Partner changed (or squad broke up) - drop cached gear from the previous partner
      this.otherMemberActiveSkin = undefined;
      this.otherMemberEquippedHat = undefined;
      this.otherMemberWeapon = null;
    }
    this.otherMember = otherMember;

    this.refreshVisibility();
  }

  updateMemberData(data: SquadUpdateData): void {
    if (!this.otherMember || data.accountId !== this.otherMember.accountId) return;

    let gearChanged = false;
    if (data.activeSkin !== undefined) { this.otherMemberActiveSkin = data.activeSkin; gearChanged = true; }
    if (data.equippedHat !== undefined) { this.otherMemberEquippedHat = data.equippedHat; gearChanged = true; }
    if (data.weapon !== undefined) { this.otherMemberWeapon = data.weapon; gearChanged = true; }
    if (data.status !== undefined) { this.otherMember = { ...this.otherMember, status: data.status }; }

    if (gearChanged && this.sprite.visible) {
      this.syncSprite();
    }
    this.updateStatusLabel();
  }

  clear(): void {
    this.otherMember = null;
    this.otherMemberActiveSkin = undefined;
    this.otherMemberEquippedHat = undefined;
    this.otherMemberWeapon = null;
    this.refreshVisibility();
  }

  /** Call wherever the host toggles its own player-sprite visibility (screen show/hide, storage/equipment-expanded states). */
  setScreenVisible(visible: boolean): void {
    this.screenVisible = visible;
    this.refreshVisibility();
  }

  private refreshVisibility(): void {
    const shouldShow = this.screenVisible && !!this.otherMember;
    this.sprite.setVisible(shouldShow);

    if (shouldShow) {
      this.syncSprite();
      this.createNameDisplay();
    } else {
      this.weaponSprite.setVisible(false);
      this.hatSprite.setVisible(false);
      this.removeNameDisplay();
    }
  }

  private syncSprite(): void {
    const desiredTexture = this.otherMemberActiveSkin || "player";
    const textureKey = this.scene.textures.exists(desiredTexture) ? desiredTexture : "player";

    if (this.scene.anims.exists(this.idleAnimKey)) {
      this.scene.anims.remove(this.idleAnimKey);
    }
    this.scene.anims.create({
      key: this.idleAnimKey,
      frames: this.scene.anims.generateFrameNumbers(textureKey, { start: 0, end: 1 }),
      frameRate: 4,
      repeat: -1,
    });

    this.sprite.setTexture(textureKey, 0);
    this.sprite.play(this.idleAnimKey, true);

    const weapon = this.otherMemberWeapon;
    if (weapon && weapon.definition?.spriteSheet === "weapon") {
      const frame = weapon.definition.spriteY * 10 + weapon.definition.spriteX;
      this.weaponSprite.setFrame(frame);
      this.weaponSprite.setVisible(true);
    } else {
      this.weaponSprite.setVisible(false);
    }

    const hatTextureKey = getHatSpriteKey(this.otherMemberEquippedHat);
    if (hatTextureKey && this.scene.textures.exists(hatTextureKey)) {
      this.hatSprite.setTexture(hatTextureKey);
      this.hatSprite.setVisible(true);
    } else {
      this.hatSprite.setVisible(false);
    }
  }

  private createNameDisplay(): void {
    this.removeNameDisplay();
    if (!this.otherMember) return;

    const nameY = this.pedestalY - 140 * SQUAD_SCALE_RATIO;
    this.nameText = this.scene.add.text(
      this.squadPlayerX,
      nameY,
      this.otherMember.name,
      { fontSize: "16px", color: COLORS.YELLOW, fontFamily: "Anton", align: "center" },
    );
    this.nameText.setOrigin(0.5, 0.5);
    this.nameText.setScrollFactor(0);

    const padX = 10;
    const padY = 6;
    this.nameBg = this.scene.add.graphics();
    this.nameBg.fillStyle(0x000000, 0.55);
    this.nameBg.fillRoundedRect(
      this.squadPlayerX - this.nameText.width / 2 - padX,
      nameY - this.nameText.height / 2 - padY,
      this.nameText.width + padX * 2,
      this.nameText.height + padY * 2,
      8,
    );
    this.nameBg.setScrollFactor(0);

    const statusY = nameY + this.nameText.height / 2 + padY + 12;
    this.statusText = this.scene.add.text(
      this.squadPlayerX,
      statusY,
      "",
      { fontSize: "13px", color: COLORS.BLACK, fontFamily: "Anton", align: "center" },
    );
    this.statusText.setOrigin(0.5, 0.5);
    this.statusText.setScrollFactor(0);

    this.statusBg = this.scene.add.graphics();
    this.statusBg.setScrollFactor(0);

    this.panel.add([this.nameBg, this.nameText, this.statusBg, this.statusText]);
    this.updateStatusLabel();
  }

  private removeNameDisplay(): void {
    if (this.nameBg) {
      this.panel.remove(this.nameBg, true);
      this.nameBg = null;
    }
    if (this.nameText) {
      this.panel.remove(this.nameText, true);
      this.nameText = null;
    }
    if (this.statusBg) {
      this.panel.remove(this.statusBg, true);
      this.statusBg = null;
    }
    if (this.statusText) {
      this.panel.remove(this.statusText, true);
      this.statusText = null;
    }
  }

  private updateStatusLabel(): void {
    if (!this.statusText || !this.statusBg || !this.otherMember) return;
    const member = this.otherMember;

    let label: string;
    let color: string;
    if (member.status === "Match") {
      label = "In a match";
      color = COLORS.YELLOW;
    } else if (!member.connected) {
      label = "Offline";
      color = COLORS.GRAY;
    } else if (member.ready) {
      label = "Ready";
      color = COLORS.GREEN;
    } else {
      label = "Not Ready";
      color = COLORS.RED;
    }
    this.statusText.setText(label);
    this.statusText.setColor(COLORS.BLACK);

    const padX = 8;
    const padY = 4;
    this.statusBg.clear();
    this.statusBg.fillStyle(hexToPhaser(color), 1);
    this.statusBg.fillRoundedRect(
      this.statusText.x - this.statusText.width / 2 - padX,
      this.statusText.y - this.statusText.height / 2 - padY,
      this.statusText.width + padX * 2,
      this.statusText.height + padY * 2,
      6,
    );
  }
}
